import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { capitalize } from 'lodash';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'src/components/form/select';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import type { ResultType } from '../modals/types';

interface Props {
  readOnly: boolean;
  onChange?: (val: ResultType) => void;
  parent?: ObjectWithContributors;
  value: ResultType;
  testId?: string;
}

const TypeSelector: FC<Props> = ({
  readOnly,
  onChange,
  parent,
  value,
  testId,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'assessmentResults.fields',
  });
  const { t: tt } = useTranslation('taxonomy');

  const policyModuleEnabled = useIsModuleEnabled('document');
  const complianceModuleEnabled = useIsModuleEnabled('obligation');

  const {
    hasPermission: canCreateDocumentAssessmentResult,
    loading: canCreateDocumentLoading,
  } = useHasPermissionQuery('insert:document_assessment_result', parent);
  const {
    hasPermission: canCreateObligationAssessmentResult,
    loading: canCreateObligationLoading,
  } = useHasPermissionQuery('insert:obligation_assessment_result', parent);
  const {
    hasPermission: canCreateRiskAssessmentResult,
    loading: canCreateRiskLoading,
  } = useHasPermissionQuery('insert:risk_assessment_result', parent);

  const options = [];

  if (
    canCreateDocumentAssessmentResult &&
    !canCreateDocumentLoading &&
    policyModuleEnabled
  ) {
    options.push({
      value: Parent_Type_Enum.DocumentAssessmentResult,
      label: capitalize(tt('document_one')),
    });
  }

  if (canCreateRiskAssessmentResult && !canCreateRiskLoading) {
    options.push({
      value: Parent_Type_Enum.RiskAssessmentResult,
      label: capitalize(tt('risk_one')),
    });
  }

  if (
    canCreateObligationAssessmentResult &&
    !canCreateObligationLoading &&
    complianceModuleEnabled
  ) {
    options.push({
      value: Parent_Type_Enum.ObligationAssessmentResult,
      label: capitalize(tt('obligation_one')),
    });
  }

  return (
    <FormField label={t('Type')} data-testid={`form-field-${testId}`}>
      <Select
        disabled={readOnly || options.length === 1}
        onChange={(e) =>
          onChange?.(e.detail.selectedOption.value as ResultType)
        }
        options={options}
        selectedOption={options.find((o) => o.value === value) || null}
        placeholder={t('Type_placeholder')}
        expandToViewport
      />
    </FormField>
  );
};

export default TypeSelector;
